import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AuthImage from "./AuthImage";
import Lightbox from "./Lightbox";
import Modal from "./Modal";
import { ExternalLink, Play, ImageIcon, Link2, ChevronRight } from "lucide-react";

/**
 * Read-only media gallery (Videos / Photos / Web Links) for a profile page.
 *
 * Credit-efficient: one GET per tab per page-load. No polling, no
 * intersection observers, no per-item subqueries. The full-gallery modal
 * paginates with offset/limit only when the user explicitly opens it.
 */
const TABS = [
  { key: "video", label: "Videos", icon: Play },
  { key: "photo", label: "Photos", icon: ImageIcon },
  { key: "link", label: "Web Links", icon: Link2 },
];

function authedFileUrl(url) {
  if (!url) return "";
  const tok = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;
  const sep = url.includes("?") ? "&" : "?";
  return tok ? `${url}${sep}auth=${encodeURIComponent(tok)}` : url;
}

function VideoThumb({ item, onPlay }) {
  return (
    <button
      type="button"
      onClick={onPlay}
      className="group relative aspect-video w-full overflow-hidden rounded-md border border-[rgba(0,170,255,0.18)] bg-black hover:border-[#00aaff] transition-colors"
      data-testid={`gallery-video-thumb-${item.file_id}`}
      title="Play video"
    >
      <video
        src={authedFileUrl(item.url)}
        preload="metadata"
        muted
        playsInline
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20 transition-colors">
        <Play size={28} className="text-white drop-shadow-lg" fill="white" />
      </div>
    </button>
  );
}

function PhotoThumb({ item, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="aspect-square w-full overflow-hidden rounded-md border border-[rgba(0,170,255,0.18)] hover:border-[#00aaff] transition-colors cursor-zoom-in"
      data-testid={`gallery-photo-thumb-${item.file_id}`}
    >
      <AuthImage src={item.url} alt="" className="w-full h-full object-cover" />
    </button>
  );
}

function LinkThumb({ item }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col rounded-md border border-[rgba(0,170,255,0.18)] hover:border-[#00aaff] overflow-hidden bg-[#0d1b2e]/40 transition-colors"
      data-testid={`gallery-link-thumb-${item.post_id}`}
    >
      <div className="aspect-video w-full bg-black/40 flex items-center justify-center overflow-hidden">
        {item.image ? (
          <img
            src={item.image}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <Link2 size={26} className="text-[#94a3b8]" />
        )}
      </div>
      <div className="px-2.5 py-2 min-w-0">
        <div className="font-mono-tech text-[9px] uppercase tracking-[0.18em] text-[#94a3b8] truncate">
          {item.site_name || new URL(item.url).hostname.replace(/^www\./, "")}
        </div>
        <div className="text-xs text-white truncate group-hover:text-[#00aaff]">
          {item.title || item.url}
        </div>
      </div>
    </a>
  );
}

function EmptyState({ kind }) {
  const messages = {
    video: "No videos yet.",
    photo: "No photos yet.",
    link: "No web links shared yet.",
  };
  return (
    <div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#94a3b8] py-8 text-center" data-testid={`gallery-empty-${kind}`}>
      {messages[kind]}
    </div>
  );
}

function Grid({ kind, items, onPlayVideo, onOpenPhoto }) {
  if (!items.length) return <EmptyState kind={kind} />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
      {items.map((item) => {
        if (kind === "video") return <VideoThumb key={item.file_id} item={item} onPlay={() => onPlayVideo(item)} />;
        if (kind === "photo") return <PhotoThumb key={item.file_id} item={item} onOpen={() => onOpenPhoto(item)} />;
        return <LinkThumb key={`${item.post_id}-${item.url}`} item={item} />;
      })}
    </div>
  );
}

export default function ProfileMediaGallery({ profileId }) {
  const [data, setData] = useState({ video: null, photo: null, link: null });
  const [seeAllKind, setSeeAllKind] = useState(null);
  const [seeAllItems, setSeeAllItems] = useState([]);
  const [seeAllLoading, setSeeAllLoading] = useState(false);
  const [seeAllOffset, setSeeAllOffset] = useState(0);
  const [seeAllCount, setSeeAllCount] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [photoLightbox, setPhotoLightbox] = useState(null); // { items, startIndex }

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    Promise.all(
      TABS.map((t) =>
        api
          .get(`/profile/${profileId}/media`, { params: { kind: t.key, limit: 6 } })
          .then((r) => [t.key, r.data])
          .catch(() => [t.key, { items: [], count: 0 }])
      )
    ).then((results) => {
      if (cancelled) return;
      const next = {};
      results.forEach(([k, v]) => { next[k] = v; });
      setData(next);
    });
    return () => { cancelled = true; };
  }, [profileId]);

  const openSeeAll = async (kind) => {
    setSeeAllKind(kind);
    setSeeAllItems([]);
    setSeeAllOffset(0);
    setSeeAllLoading(true);
    try {
      const { data: d } = await api.get(`/profile/${profileId}/media`, {
        params: { kind, limit: 24, offset: 0 },
      });
      setSeeAllItems(d.items || []);
      setSeeAllCount(d.count || 0);
      setSeeAllOffset((d.items || []).length);
    } finally {
      setSeeAllLoading(false);
    }
  };

  const loadMoreSeeAll = async () => {
    setSeeAllLoading(true);
    try {
      const { data: d } = await api.get(`/profile/${profileId}/media`, {
        params: { kind: seeAllKind, limit: 24, offset: seeAllOffset },
      });
      setSeeAllItems((prev) => [...prev, ...(d.items || [])]);
      setSeeAllOffset((prev) => prev + (d.items || []).length);
    } finally {
      setSeeAllLoading(false);
    }
  };

  const handlePlayVideo = (item) => setPlayingVideo(item);
  const handleOpenPhoto = (item) => {
    const photos = data.photo?.items || [];
    const idx = photos.findIndex((p) => p.file_id === item.file_id);
    setPhotoLightbox({
      images: photos.map((p) => ({ url: p.url, file_id: p.file_id })),
      startIndex: idx >= 0 ? idx : 0,
    });
  };

  return (
    <section className="csc-card p-5 space-y-5" data-testid="profile-media-gallery">
      <div className="flex items-center justify-between">
        <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">Media</h2>
        <div className="text-[10px] font-mono-tech text-[#94a3b8]">
          {data.video?.count || 0} · {data.photo?.count || 0} · {data.link?.count || 0}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {TABS.map((t) => {
          const Icon = t.icon;
          const block = data[t.key];
          const items = block?.items || [];
          const count = block?.count || 0;
          return (
            <div key={t.key} className="space-y-3" data-testid={`gallery-block-${t.key}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-white">
                  <Icon size={14} className="text-[#00aaff]" /> {t.label}
                  <span className="font-mono-tech text-[10px] text-[#94a3b8]">({count})</span>
                </div>
                {count > items.length && (
                  <button
                    onClick={() => openSeeAll(t.key)}
                    className="font-mono-tech text-[10px] uppercase tracking-wider text-[#00aaff] hover:text-[#39ff14] flex items-center gap-1"
                    data-testid={`gallery-see-all-${t.key}`}
                  >
                    See All <ChevronRight size={11} />
                  </button>
                )}
              </div>
              <Grid
                kind={t.key}
                items={items}
                onPlayVideo={handlePlayVideo}
                onOpenPhoto={handleOpenPhoto}
              />
            </div>
          );
        })}
      </div>

      {/* Full-gallery modal */}
      <Modal
        open={!!seeAllKind}
        onClose={() => setSeeAllKind(null)}
        className="p-6 max-w-4xl"
        ariaLabel="Full media gallery"
        data-testid="gallery-see-all-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">
            All {seeAllKind === "video" ? "Videos" : seeAllKind === "photo" ? "Photos" : "Web Links"} ({seeAllCount})
          </h2>
        </div>
        <Grid
          kind={seeAllKind}
          items={seeAllItems}
          onPlayVideo={handlePlayVideo}
          onOpenPhoto={handleOpenPhoto}
        />
        {seeAllOffset < seeAllCount && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={loadMoreSeeAll}
              disabled={seeAllLoading}
              className="csc-btn-ghost"
              data-testid="gallery-load-more"
            >
              {seeAllLoading ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </Modal>

      {/* Inline video player modal */}
      <Modal
        open={!!playingVideo}
        onClose={() => setPlayingVideo(null)}
        className="p-3 max-w-3xl"
        ariaLabel="Play video"
        data-testid="gallery-video-player-modal"
      >
        {playingVideo && (
          <>
            <video
              controls
              autoPlay
              playsInline
              className="w-full max-h-[80vh] bg-black rounded"
              src={authedFileUrl(playingVideo.url)}
            />
            <div className="mt-2 flex justify-end">
              <a
                href={authedFileUrl(playingVideo.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="csc-btn-ghost !text-xs"
              >
                <ExternalLink size={11} /> Open in new tab
              </a>
            </div>
          </>
        )}
      </Modal>

      {photoLightbox && (
        <Lightbox
          images={photoLightbox.images}
          startIndex={photoLightbox.startIndex}
          onClose={() => setPhotoLightbox(null)}
        />
      )}
    </section>
  );
}
